import { Component, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NbDialogRef, NbTabComponent } from '@nebular/theme';
import { forkJoin } from 'rxjs';
import { ContractEditorComponent } from '../../contract-editor/contract-editor.component';
import { AuthService } from '../../../services/auth.service';
import {
  ClientTemplateAccess,
  Organisation,
  OrganisationFeatures,
  OrganisationService,
} from '../../../services/organisation.service';

@Component({
  selector: 'app-organisation-dialog',
  templateUrl: './organisation-dialog.component.html',
  styleUrl: './organisation-dialog.component.scss',
})
export class OrganisationDialogComponent {
  @Input() data: Organisation | undefined = undefined;

  form: FormGroup;

  /** Non-public bot templates with a per-row `granted` flag. Loaded
   *  once when the dialog opens (super_admin only — endpoint returns
   *  403 otherwise, which we just treat as "no list to show"). The
   *  toggle state is mutated in place; on save we ship the full set
   *  of granted ids to the atomic-replace endpoint. */
  templateAccess: ClientTemplateAccess[] = [];
  templateAccessLoaded = false;

  /** Reiter „Allgemein“ / „Vertrag“ (Vertrag nur fuer Super-Admins). Der
   *  Footer wechselt mit: im Vertragsreiter gehoert die Primaraktion dem
   *  Vertrag (Regel 16 — Enter klickt die letzte Primaraktion im Footer). */
  activeTab: 'general' | 'contract' = 'general';
  contractTabOpened = false;
  /** Ein Vertrag wurde aktiviert, geaendert oder beendet — die Liste muss
   *  danach neu laden (Plan-Spalte, Vertragsmarke). */
  contractChanged = false;

  @ViewChild(ContractEditorComponent) contractEditor?: ContractEditorComponent;

  constructor(
    protected dialogRef: NbDialogRef<OrganisationDialogComponent>,
    protected organisationService: OrganisationService,
    protected authService: AuthService,
    protected fb: FormBuilder
  ) {
    this.form = this.fb.group({
      company_name: '',
      features: '',
      // SCORM-Export im Creator (Migration 085); nur Super-Admins sehen und
      // speichern ihn, fuer alle anderen verwirft das Backend das Feld
      creator_export_allowed: true,
    });
  }

  ngOnInit(): void {
    if (this.data) {
      this.form.patchValue({
        ...this.data,
        creator_export_allowed: this.data.creator_export_allowed !== false,
      });
      // Only super_admin gets the template-access pane. Other roles
      // would 403 on the call; skip it silently to avoid noise.
      if (this.isSuperAdmin) {
        this.organisationService
          .listClientTemplateAccess(this.data.id)
          .subscribe({
            next: (rows) => {
              this.templateAccess = rows;
              this.templateAccessLoaded = true;
            },
            error: () => {
              this.templateAccessLoaded = true;
            },
          });
      }
    }
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const calls: any[] = [
      this.organisationService.updateOrganisation({
        id: this.data?.id,
        ...(this.form.value as any),
      }),
    ];

    // Only push the template-access save when we actually loaded the
    // list (super_admin opened the dialog AND the GET succeeded). For
    // other roles or load failures, the toggle pane wasn't shown so
    // there's nothing meaningful to persist.
    if (this.isSuperAdmin && this.templateAccessLoaded && this.data) {
      const grantedIds = this.templateAccess
        .filter((t) => t.granted)
        .map((t) => t.template_id);
      calls.push(
        this.organisationService.replaceClientTemplateAccess(
          this.data.id,
          grantedIds
        )
      );
    }

    forkJoin(calls).subscribe(() => {
      this.dialogRef.close(this.contractChanged);
    });
  }

  close(): void {
    this.dialogRef.close(this.contractChanged);
  }

  onTabChange(tab: NbTabComponent): void {
    this.activeTab = tab?.tabId === 'contract' ? 'contract' : 'general';
    if (this.activeTab === 'contract') {
      this.contractTabOpened = true;
    }
  }

  // `!!` haelt die Werte stabil, solange der Editor noch nicht existiert
  // (undefined -> false), sonst meldet der Dev-Modus
  // ExpressionChangedAfterItHasBeenChecked beim ersten Oeffnen des Reiters.
  get contractIsActive(): boolean {
    return !!this.contractEditor?.isActive;
  }

  get contractReady(): boolean {
    return !!this.contractEditor?.ready;
  }

  get contractBusy(): boolean {
    return !!this.contractEditor?.busy;
  }

  get contractSaving(): boolean {
    return !!this.contractEditor?.saving;
  }

  get contractEnding(): boolean {
    return !!this.contractEditor?.ending;
  }

  saveContract(): void {
    this.contractEditor?.submit();
  }

  endContract(): void {
    this.contractEditor?.endContract();
  }

  toggleTemplate(row: ClientTemplateAccess): void {
    row.granted = !row.granted;
  }

  get isSuperAdmin(): boolean {
    return this.authService.accessTokenPayload$.value?.role === 'super_admin';
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }

  get features() {
    return Object.keys(OrganisationFeatures).map((key) => key);
  }
}
